// Profile validation gate — the thing whose absence caused the live defect.
//
// On 2026-06-17 a full diagnostic produced cogniprints/Q0cRNAnKWPbr5B75GOchO2cWLzQ2:
// diagnosticType 'full', questionsAsked 0, all three paid dimensions null, and all
// four free dimensions sitting on their exact hard-coded fallbacks (20/20/20/20/20,
// 50/50, 34/33/33). Not one number on that document came from the learner, and the
// summary generator then narrated the invented centre values back as an insight.
//
// The cause was that transformAIProfile() had no failure path: a payload with no
// `dimensions` object still produced a structurally valid CogniPrintProfile, so the
// caller saw isComplete === true and persisted it.
//
// validateProfile() is that failure path. A profile that does not pass is not saved.
// See docs/intent/001-diagnostic-redesign/spec.md §1.3.

import type { CogniPrintProfile, DiagnosticDimensionKey } from '../types/cogniprint';
import { FREE_DIMENSION_KEYS, PAID_DIMENSION_KEYS } from '../types/cogniprint';
import type { DiagnosticMode } from '../types/diagnostic';
import { validateWeightedSum } from './cogniprint-scoring';

export interface ProfileValidationResult {
  ok: boolean;
  /** Machine-readable failure codes, safe to put in an analytics event. */
  reasons: string[];
}

/** Provenance values that mean "this is not a finding and must never be shown as one". */
const NON_EVIDENCE_PROVENANCE = new Set(['defaulted', 'not_assessed']);

function provenanceOf(
  profile: CogniPrintProfile,
  key: DiagnosticDimensionKey,
): string | undefined {
  return profile.dimensionProvenance?.[key];
}

/** True when a dimension carries real evidence from this learner. */
export function isAssessed(profile: CogniPrintProfile, key: DiagnosticDimensionKey): boolean {
  const provenance = provenanceOf(profile, key);
  if (provenance) return !NON_EVIDENCE_PROVENANCE.has(provenance);
  // Legacy profile with no provenance: a non-null paid dimension is the best
  // available signal. Free dimensions are never null, so they cannot be judged
  // this way and are treated as assessed — normalizeProfile() backfills them.
  if (PAID_DIMENSION_KEYS.includes(key)) {
    return profile[key as 'infoDensity' | 'motivation' | 'viewpointRange'] != null;
  }
  return true;
}

function inRange(n: unknown, lo: number, hi: number): boolean {
  return typeof n === 'number' && Number.isFinite(n) && n >= lo && n <= hi;
}

/**
 * Decide whether a profile may be persisted. Returns every reason it may not,
 * rather than the first, so the analytics event carries the full picture.
 */
export function validateProfile(
  profile: CogniPrintProfile,
  mode: DiagnosticMode,
): ProfileValidationResult {
  const reasons: string[] = [];

  // ─── The defect signature ───────────────────────────────────────────────

  const assessedFree = FREE_DIMENSION_KEYS.filter((k) => isAssessed(profile, k));
  if (assessedFree.length === 0) {
    reasons.push('no_assessed_free_dimensions');
  }

  if (mode === 'full') {
    const assessedPaid = PAID_DIMENSION_KEYS.filter((k) => isAssessed(profile, k));
    if (assessedPaid.length === 0) {
      reasons.push('full_mode_no_assessed_paid_dimensions');
    }
  }

  const anyAssessed = [...FREE_DIMENSION_KEYS, ...PAID_DIMENSION_KEYS].some((k) =>
    isAssessed(profile, k),
  );
  if (anyAssessed && (profile.questionsAsked ?? 0) <= 0 && !profile.isTestProfile) {
    reasons.push('questions_asked_zero_with_assessed_dimensions');
  }

  // ─── Structural rails, re-asserted at the gate ──────────────────────────
  // sanitizeProfile() already clamps these. Asserting them here means a code
  // path that skips the sanitizer cannot slip a malformed profile past.

  if (!inRange(profile.contentFormat?.confidence, 0, 1)) reasons.push('contentFormat_confidence_out_of_range');
  if (!inRange(profile.learningApproach?.confidence, 0, 1)) reasons.push('learningApproach_confidence_out_of_range');
  if (!inRange(profile.depthTolerance?.confidence, 0, 1)) reasons.push('depthTolerance_confidence_out_of_range');
  if (!inRange(profile.sequencing?.confidence, 0, 1)) reasons.push('sequencing_confidence_out_of_range');
  if (!inRange(profile.overallConfidence, 0, 1)) reasons.push('overallConfidence_out_of_range');

  if (!inRange(profile.depthTolerance?.score, 0, 100)) reasons.push('depthTolerance_score_out_of_range');
  if (!inRange(profile.learningApproach?.score?.theoryApplication, 0, 100)) reasons.push('theoryApplication_out_of_range');
  if (!inRange(profile.learningApproach?.score?.reflectiveActive, 0, 100)) reasons.push('reflectiveActive_out_of_range');

  if (profile.contentFormat?.score && !validateWeightedSum({ ...profile.contentFormat.score })) {
    reasons.push('contentFormat_weights_do_not_sum_to_100');
  }
  if (profile.sequencing?.score && !validateWeightedSum({ ...profile.sequencing.score })) {
    reasons.push('sequencing_weights_do_not_sum_to_100');
  }

  for (const key of PAID_DIMENSION_KEYS) {
    const dim = profile[key as 'infoDensity' | 'motivation' | 'viewpointRange'];
    if (dim == null) continue;
    if (!inRange(dim.confidence, 0, 1)) reasons.push(`${key}_confidence_out_of_range`);
    if (key === 'motivation') {
      if (!validateWeightedSum({ ...profile.motivation!.score })) reasons.push('motivation_weights_do_not_sum_to_100');
    } else if (!inRange(dim.score as number, 0, 100)) {
      reasons.push(`${key}_score_out_of_range`);
    }
  }

  return { ok: reasons.length === 0, reasons };
}

/**
 * A profile written by the Admin Content Lab is test data. It must never be
 * rendered to a learner, narrated, or treated as evidence — see D-7 in the spec.
 */
export function isTestProfile(profile: Pick<CogniPrintProfile, 'isTestProfile' | 'profileVersion'>): boolean {
  return profile.isTestProfile === true || profile.profileVersion === 999;
}
